import React, { useState, useContext } from 'react';
import styles from './styles.module.css';
import CloseIcon from '@material-ui/icons/Close';
import StoreIcon from '@material-ui/icons/Store';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIcon from '@material-ui/icons/Phone';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import StarIcon from '@material-ui/icons/Star';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import BusinessIcon from '@material-ui/icons/Business';
import InfoIcon from '@material-ui/icons/Info';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import PeopleIcon from '@material-ui/icons/People';
import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Chip,
} from '@material-ui/core';
import { SUBSCRIPTIONS } from '../../const';
import ShopServices from '../../services/ShopServices';
import { AlertMessagesContext } from 'react-alert-messages';

function ShopDetails(props) {
  const { handleClose, data, resetShop, onUnblock } = props;
  const { postAlertMessage } = useContext(AlertMessagesContext);

  // Local copy of users so unblock updates reflect immediately
  const [users, setUsers] = useState(data?.users || []);
  const [unblockingId, setUnblockingId] = useState(null);

  const handleUnblock = async (userId) => {
    setUnblockingId(userId);
    try {
      await ShopServices.unblockUser(userId);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, d_is_blocked: false } : u)));
      if (onUnblock) onUnblock(userId);
      postAlertMessage({ text: 'User unblocked successfully', type: 'success' });
    } catch (error) {
      postAlertMessage({ text: error.message || 'Failed to unblock user', type: 'failed' });
    }
    setUnblockingId(null);
  };

  const getInitialFromDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const getInitialToDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
  };

  const [filterFromDate, setFilterFromDate] = useState(getInitialFromDate());
  const [filterToDate, setFilterToDate] = useState(getInitialToDate());
  const [salesCount, setSalesCount] = useState(null);
  const [salesCountLoading, setSalesCountLoading] = useState(false);

  const fetchSalesCount = async () => {
    if (!data?.id) return;
    setSalesCountLoading(true);
    try {
      const fromParts = filterFromDate.split('-');
      const toParts = filterToDate.split('-');
      const startOfMonth = new Date(Date.UTC(Number(fromParts[0]), Number(fromParts[1]) - 1, Number(fromParts[2]), 0, 0, 0, 0));
      const endOfMonth = new Date(Date.UTC(Number(toParts[0]), Number(toParts[1]) - 1, Number(toParts[2]), 23, 59, 59, 999));
      
      const body = {
        filter_start_time: startOfMonth.toISOString(),
        filter_end_time: endOfMonth.toISOString(),
      };

      const res = await ShopServices.getShopSalesCount(data.id, body);
      if (res?.data && res.data.sales_count !== undefined) {
        setSalesCount(res.data.sales_count);
      } else {
        setSalesCount('N/A');
      }
    } catch (err) {
      console.error(err);
      setSalesCount('Error');
    }
    setSalesCountLoading(false);
  };

  const getSubscriptionBadgeClass = (subKey) => {
    if (!subKey) return styles.badgeFree;
    const sub = String(subKey).toLowerCase();
    if (sub.includes('premium') || sub.includes('gold') || sub.includes('pro')) {
      return styles.badgePremium;
    }
    if (sub.includes('standard') || sub.includes('basic')) {
      return styles.badgeStandard;
    }
    return styles.badgeFree;
  };

  const getSubscriptionStatusBadge = (endDateStr) => {
    if (!endDateStr) return { label: 'No Plan', class: styles.badgeExpired };
    
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(endDateStr);
    endDate.setHours(0, 0, 0, 0);

    if (endDate < currentDate) {
      return { label: 'Expired', class: styles.badgeExpired };
    }

    const diffTime = endDate - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 5) {
      return { label: `Expiring soon (${diffDays}d)`, class: styles.badgeWarning };
    }

    return { label: 'Active', class: styles.badgeActive };
  };

  const renderValidityValue = (dateStr) => {
    if (!dateStr || dateStr === '-') return <span style={{ color: '#94a3b8' }}>-</span>;
    
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const valDate = new Date(dateStr);
    valDate.setHours(0, 0, 0, 0);
    
    const isExpired = valDate < currentDate;
    return (
      <span style={{ color: isExpired ? '#ef4444' : '#10b981', fontWeight: 700 }}>
        {dateStr}
      </span>
    );
  };

  return (
    <Dialog
      open={true}
      maxWidth="md"
      fullWidth={true}
      classes={{ paper: styles.dialogPaper }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle className={styles.detailsHeader} disableTypography id="alert-dialog-title">
        <h2 className={styles.detailsHeaderTitle}>
          <StoreIcon fontSize="medium" />
          <span>Shop Details</span>
        </h2>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className={styles.detailsCloseButton}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.detailsContent}>
        <Grid container spacing={3}>
          {/* Shop Profile Column */}
          <Grid item xs={12} md={6}>
            <div className={styles.cardContainer}>
              <div className={styles.sectionHeader}>
                <BusinessIcon fontSize="small" />
                <span>Shop Profile</span>
              </div>
              
              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><InfoIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Shop ID</span>
                <span className={`${styles.infoValue} ${styles.highlightValue}`}>{data?.id || '-'}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><StoreIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Shop Name</span>
                <span className={`${styles.infoValue} ${styles.highlightValue}`}>{data?.shop_name || '-'}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><PersonIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Dealer</span>
                <span className={styles.infoValue}>{data?.dealer?.name || '-'}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><PhoneIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Mobile No</span>
                <span className={styles.infoValue}>{data?.mobile_no || '-'}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><MonetizationOnIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Currency</span>
                <span className={styles.infoValue}>{data?.currency || '-'}</span>
              </div>

              <div className={styles.usersSubHeader}>
                <PeopleIcon fontSize="small" />
                <span>Users</span>
              </div>

              {users.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: '13px', padding: '8px 0' }}>No users found</div>
              ) : (
                users.map((user) => (
                  <div key={user.id} className={styles.userRow}>
                    <div className={styles.userInfo}>
                      <PersonIcon fontSize="small" className={styles.userIcon} />
                      <div>
                        <div className={styles.userName}>{user.username}</div>
                        {user.id && (
                          <div className={styles.userId}>ID: {user.id}</div>
                        )}
                      </div>
                    </div>
                    <div className={styles.userActions}>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={!user.d_is_blocked}
                          disabled={unblockingId === user.id}
                          onChange={() => {
                            if (user.d_is_blocked) handleUnblock(user.id);
                          }}
                        />
                        <span className={styles.toggleSlider} />
                      </label>
                      <span className={user.d_is_blocked ? styles.userStatusBlocked : styles.userStatusActive}>
                        {unblockingId === user.id ? 'Updating...' : user.d_is_blocked ? 'Blocked' : 'Active'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Grid>

          {/* Subscription & Features Column */}
          <Grid item xs={12} md={6}>
            <div className={styles.cardContainer}>
              <div className={styles.sectionHeader}>
                <CalendarTodayIcon fontSize="small" />
                <span>Subscription & Features</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><StarIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Subscription</span>
                <span className={styles.infoValue}>
                  <Chip
                    label={data?.subscription?.name || SUBSCRIPTIONS[data?.subscription] || 'Free'}
                    className={`${styles.dateChip} ${getSubscriptionBadgeClass(data?.subscription?.name || data?.subscription)}`}
                    size="small"
                  />
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><InfoIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Max Products</span>
                <span className={styles.infoValue}>
                  {data?.subscription?.max_products !== undefined && data?.subscription?.max_products !== null
                    ? data?.subscription?.max_products
                    : '-'}
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><InfoIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Max Sales Per Month</span>
                <span className={styles.infoValue}>
                  {data?.subscription?.max_sales_limit !== undefined && data?.subscription?.max_sales_limit !== null
                    ? data?.subscription?.max_sales_limit
                    : (data?.subscription?.max_sales !== undefined && data?.subscription?.max_sales !== null
                        ? data?.subscription?.max_sales
                        : '-')}
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><CalendarTodayIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>End Date</span>
                <span className={styles.infoValue} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                  <span>{data?.subscription_end_date || '-'}</span>
                  {data?.subscription_end_date && (
                    <Chip
                      label={getSubscriptionStatusBadge(data?.subscription_end_date).label}
                      className={`${styles.dateChip} ${getSubscriptionStatusBadge(data?.subscription_end_date).class}`}
                      size="small"
                    />
                  )}
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.iconWrapper}><AccountBalanceWalletIcon fontSize="small" /></span>
                <span className={styles.infoLabel}>Additional Sales Balance</span>
                <span className={styles.infoValue}>{data?.additional_sales_balance || 0}</span>
              </div>

              <div style={{ marginTop: '20px', marginBottom: '8px', fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <EqualizerIcon fontSize="small" />
                <span>Feature Validities</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.infoLabel} style={{ paddingLeft: '4px' }}>Expense Validity</span>
                <span className={styles.infoValue}>{renderValidityValue(data?.expense_validity)}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.infoLabel} style={{ paddingLeft: '4px' }}>Image Support Validity</span>
                <span className={styles.infoValue}>{renderValidityValue(data?.image_support_validity)}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.infoLabel} style={{ paddingLeft: '4px' }}>KOT Validity</span>
                <span className={styles.infoValue}>{renderValidityValue(data?.kot_validity)}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.infoLabel} style={{ paddingLeft: '4px' }}>KOT Lite Validity</span>
                <span className={styles.infoValue}>{renderValidityValue(data?.kot_lite_validity)}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.infoLabel} style={{ paddingLeft: '4px' }}>Smart Scale Validity</span>
                <span className={styles.infoValue}>{renderValidityValue(data?.smart_scale_validity)}</span>
              </div>

              <div style={{ marginTop: '20px', marginBottom: '8px', fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <EqualizerIcon fontSize="small" />
                <span>Sales Count Query</span>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>From Date</label>
                  <input
                    type="date"
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>To Date</label>
                  <input
                    type="date"
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  />
                </div>
                <div>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={salesCountLoading}
                    onClick={fetchSalesCount}
                    style={{
                      fontSize: '11px',
                      textTransform: 'none',
                      padding: '6px 12px',
                      height: '32px',
                      background: 'var(--primary-gradient)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  >
                    {salesCountLoading ? 'Loading...' : 'Fetch'}
                  </Button>
                </div>
              </div>

              {salesCount !== null && (
                <div className={styles.infoRow} style={{ marginTop: '12px', borderBottom: 'none' }}>
                  <span className={styles.infoLabel} style={{ paddingLeft: '4px' }}>Total Sales Count</span>
                  <span className={styles.infoValue} style={{ fontSize: '15px', fontWeight: 700, color: 'var(--primary-color)' }}>
                    {salesCount}
                  </span>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={styles.detailsActions}>
        <div className={styles.resetBtnGroup}>
          <Button variant="outlined" className={styles.softResetBtn} onClick={() => resetShop('SOFT_RESET')}>
            Soft Reset
          </Button>
          <Button variant="outlined" className={styles.hardResetBtn} onClick={() => resetShop('HARD_RESET')}>
            Hard Reset
          </Button>
        </div>
        <div>
          <Button variant="contained" className={styles.closeBtn} onClick={handleClose}>
            Close
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default ShopDetails;
